import React from 'react';
import { ACCENT_HEX, ACCENT_SOFT, type JuniorHubItem } from '../../lib/juniorTheme';
import { useJuniorAliveNav } from '../../contexts/JuniorAliveNavContext';

interface JuniorHubGridProps {
  items: JuniorHubItem[];
  columns?: 1 | 2 | 3;
  animateEntry?: boolean;
}

const COL: Record<NonNullable<JuniorHubGridProps['columns']>, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
};

export const JuniorHubGrid: React.FC<JuniorHubGridProps> = ({
  items,
  columns = 2,
  animateEntry = false,
}) => {
  const { navigateAlive } = useJuniorAliveNav();

  return (
    <div className={`grid grid-cols-1 ${COL[columns]} gap-6 sm:gap-8`}>
      {items.map((item, i) => {
        const hex = ACCENT_HEX[item.accent];
        const soft = ACCENT_SOFT[item.accent];
        const available = item.available !== false;

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => available && navigateAlive(item.path)}
            disabled={!available}
            className={`junior-card-hover group relative flex flex-col gap-5 overflow-hidden rounded-[1.75rem] p-6 sm:p-8 text-left backdrop-blur min-h-[220px] sm:min-h-[240px] ${
              animateEntry ? 'junior-alive-card' : ''
            } ${!available ? 'opacity-55 cursor-not-allowed' : ''}`}
            style={{
              animationDelay: animateEntry ? `${0.12 + i * 0.06}s` : undefined,
              background: 'rgba(255,255,255,0.92)',
              border: `2px solid ${hex}28`,
              boxShadow: `0 12px 36px -16px ${hex}55`,
            }}
          >
            <div
              className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-30 blur-2xl transition-opacity group-hover:opacity-50"
              style={{ background: hex }}
              aria-hidden
            />

            <div
              className="flex items-center justify-between border-b pt-1 pb-3"
              style={{ borderColor: `${hex}22` }}
            >
              <span className="text-xs font-bold tabular-nums tracking-widest" style={{ color: `${hex}99` }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              {!available && (
                <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: soft, color: hex }}>
                  Pronto
                </span>
              )}
              {available && (
                <span className="text-xl font-light transition-transform duration-300 group-hover:translate-x-1" style={{ color: hex }} aria-hidden>
                  →
                </span>
              )}
            </div>

            <div className="relative z-10 flex items-start gap-4">
              <span
                className="junior-float flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl text-4xl"
                style={{ background: soft, animationDelay: `${i * 0.35}s` }}
                aria-hidden
              >
                {item.emoji}
              </span>
              <div className="min-w-0 pt-1">
                <h3 className="junior-display text-[clamp(1.5rem,4vw,2.25rem)] leading-[1.05]" style={{ color: '#2A2540' }}>
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base mt-2 leading-relaxed" style={{ color: '#6B6385' }}>
                  {item.description}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default JuniorHubGrid;
